"use client";

import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import {
  posthogConsentOptions,
  registerPosthogSuperProperties,
  setupPosthogConsent,
} from "@/lib/analytics/posthogConsent";
import { readConsent } from "@/lib/consent";

const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;

/**
 * PostHog client-side initialisation. Previously wired from instrumentation-client.ts,
 * which runs before React hydration — but posthog.init() (with capture_exceptions +
 * session_recording enabled) kicks off real, asynchronous network fetches for its own
 * lazily-loaded sub-scripts (config.js, exception-autocapture.js, etc., via the /ingest
 * rewrite — a genuine external network round trip even on localhost). Those can land in
 * the DOM at an unpredictable moment that falls in the middle of React's hydration
 * reconciliation of the server-rendered tree, corrupting it: "A tree hydrated but some
 * attributes ... didn't match" — which can later surface as a live removeChild
 * NotFoundError crash when React tries to clean up a node PostHog silently displaced.
 * Reproduced live on QA (consistently on /privacy and /blog) and confirmed locally by
 * simply enabling a real PostHog key — no artificial latency needed, since /ingest's
 * sub-script fetches are already real network calls in every environment.
 *
 * Initialising inside a useEffect instead means, by React's own contract, the initial
 * hydration has already fully committed before this can run at all — there's no window
 * left for PostHog's async script loading to race it. Mounted first under <body> (see
 * app/layout.tsx) so its effect fires ahead of other components' mount-time capture
 * calls in the same commit.
 */
export function PostHogInit() {
  const initedRef = useRef(false);

  useEffect(() => {
    if (!key) return;
    // StrictMode double-invokes effects in dev; posthog.init() + setupPosthogConsent's
    // subscription aren't safe to run twice.
    if (initedRef.current) return;
    initedRef.current = true;

    const consent = readConsent();

    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "/ingest",

      // Enables History-API-based SPA pageview autocapture (capture_pageview: "history_change").
      // See: @posthog/types PostHogConfig.defaults
      defaults: "2025-05-24",

      // Capture uncaught JS errors (window.onerror, unhandledrejection).
      capture_exceptions: true,

      // Session Replay — mask all input values for GDPR compliance. Applies whenever
      // recording runs; whether it runs at all is consent-controlled below.
      session_recording: {
        maskAllInputs: true,
      },

      // persistence + disable_session_recording for the stored consent choice, so an
      // already-consented visitor is never cookieless-then-upgraded mid-load.
      ...posthogConsentOptions(consent),
    });

    // Tag every event with the deployment environment (for dashboard filtering) and the
    // visitor's cookie-consent state (so "no replay" sessions are segmentable in PostHog
    // instead of requiring a $recording_status SQL query).
    const environment = process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT;
    registerPosthogSuperProperties(posthog, consent, { environment });

    // React to banner clicks (and cross-tab changes) for the rest of the page's lifetime.
    setupPosthogConsent(posthog, { environment });
  }, []);

  return null;
}
